import { useState, useCallback } from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { operational } from "@mideye/design-tokens";
import type { AsnCount, CountryCount, FilterPanelState } from "@/types";
import { colors, cardStyles, typography } from "@/theme/eventsTokens.ts";
import {
  DateRangePanel,
  ScoreRangePanel,
  CountryPanel,
  EventStatusPanel,
  IpAddressPanel,
} from "./FilterPanels.tsx";
import { AsnPanel } from "./AsnPanel.tsx";
import { ScenariosPanel } from "./ScenariosPanel.tsx";

interface EventsFilterSidebarProps {
  readonly filters: FilterPanelState;
  readonly onFiltersChange: (filters: FilterPanelState) => void;
  readonly onApply: () => void;
  readonly onReset: () => void;
  readonly countryOptions?: CountryCount[];
  readonly asnOptions?: AsnCount[];
  readonly retentionMinDate?: Date;
  readonly retentionDays?: number;
  readonly isFetching?: boolean;
}

export default function EventsFilterSidebar({
  filters, onFiltersChange, onApply, onReset, countryOptions, asnOptions, retentionMinDate, retentionDays, isFetching,
}: EventsFilterSidebarProps) {
  const [expanded, setExpanded] = useState<string[]>(() => (filters.ipSearch ? ["date", "score", "ip"] : ["date", "score"]));
  const [scoreRange, setScoreRange] = useState<number[]>([filters.minScore, filters.maxScore]);
  const [prevScore, setPrevScore] = useState({ min: filters.minScore, max: filters.maxScore });

  if (prevScore.min !== filters.minScore || prevScore.max !== filters.maxScore) {
    setPrevScore({ min: filters.minScore, max: filters.maxScore });
    setScoreRange([filters.minScore, filters.maxScore]);
  }

  const togglePanel = (panel: string) =>
    (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded((prev) => (isExpanded ? [...prev, panel] : prev.filter((p) => p !== panel)));
    };

  const handleScoreChange = (_event: Event, v: number | number[]) => setScoreRange(v as number[]);

  const handleScoreCommit = (_event: React.SyntheticEvent | Event, v: number | number[]) => {
    const [min, max] = v as number[];
    onFiltersChange({ ...filters, minScore: min, maxScore: max });
  };

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(window.location.href);
  }, []);

  const handleReset = useCallback(() => onReset(), [onReset]);

  const panelProps = { filters, onFiltersChange };
  const scoreProps = { scoreRange, onScoreChange: handleScoreChange, onScoreCommit: handleScoreCommit };

  return (
    <Paper
      elevation={0}
      data-testid="events-sidebar"
      sx={{ ...cardStyles.default, p: 2, position: "sticky", top: 16, maxHeight: "calc(100vh - 96px)", overflowY: "auto", overscrollBehavior: "contain" }}
    >
      <SidebarHeader onCopyLink={handleCopyLink} onReset={handleReset} />
      <IpAddressPanel expanded={expanded.includes("ip")} onToggle={togglePanel("ip")} {...panelProps} />
      <DateRangePanel expanded={expanded.includes("date")} onToggle={togglePanel("date")} {...panelProps} retentionMinDate={retentionMinDate} retentionDays={retentionDays} />
      <ScoreRangePanel expanded={expanded.includes("score")} onToggle={togglePanel("score")} {...panelProps} {...scoreProps} />
      <CountryPanel expanded={expanded.includes("country")} onToggle={togglePanel("country")} {...panelProps} countryOptions={countryOptions} />
      <EventStatusPanel expanded={expanded.includes("status")} onToggle={togglePanel("status")} {...panelProps} />
      <ScenariosPanel expanded={expanded.includes("scenarios")} onToggle={togglePanel("scenarios")} {...panelProps} />
      <AsnPanel expanded={expanded.includes("asn")} onToggle={togglePanel("asn")} {...panelProps} asnOptions={asnOptions} />
      <ApplyButton onClick={onApply} disabled={isFetching} />
    </Paper>
  );
}

function ApplyButton({ onClick, disabled }: { readonly onClick: () => void; readonly disabled?: boolean }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Button
        fullWidth
        variant="contained"
        onClick={onClick}
        disabled={disabled}
        sx={{
          padding: "10px 20px",
          borderRadius: "8px",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.875rem",
          backgroundColor: colors.primary,
          color: colors.primaryForeground,
          "&:hover": { backgroundColor: operational.events.colors.actionHoverBg, boxShadow: operational.events.shadow.actionHover },
        }}
      >
        Apply Filters
      </Button>
    </Box>
  );
}

function SidebarHeader({
  onCopyLink,
  onReset,
}: {
  readonly onCopyLink: () => void;
  readonly onReset: () => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2,
      }}
    >
      <Typography sx={{ ...typography.cardTitle, mb: 0 }}>Filters</Typography>
      <Stack direction="row" spacing={1}>
        <Button
          variant="text"
          size="small"
          startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
          onClick={onCopyLink}
          sx={{
            fontSize: "0.75rem",
            minWidth: 0,
            px: 1,
            textTransform: "none",
            color: colors.textMuted,
            "&:hover": { color: colors.textPrimary },
          }}
        >
          Copy Link
        </Button>
        <Button
          variant="text"
          size="small"
          onClick={onReset}
          sx={{
            fontSize: "0.75rem",
            minWidth: 0,
            px: 1,
            textTransform: "none",
            color: colors.textMuted,
            "&:hover": { color: colors.textPrimary },
          }}
        >
          Reset
        </Button>
      </Stack>
    </Box>
  );
}
